import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
  OverlaySizeConfig,
} from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import {
  ComponentRef,
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  TemplateRef,
  untracked,
  ViewContainerRef,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { assertIsDefined } from '@ftr/contracts/shared/Assertions'
import { generateUuid } from '@ftr/contracts/type/shared'
import { FOCUSABLE_SELECTOR, focusElementInContainer, focusRelativeToElement, toHTMLElement } from '../util'
import {
  POPOVER_VIEWPORT_MARGIN,
  PopoverAlignment,
  PopoverArrowNavigation,
  PopoverPosition,
  PopoverRole,
} from './popover.constants'
import { PopoverOutlet } from './popover.outlet'

/**
 * A directive used to display content in an overlay. Unlike tooltips which are primarily text,
 * popovers can contain rich, interactive content like buttons and forms.
 *
 * The element this directive is applied to becomes the trigger element that toggles the overlay.
 * If provided, the overlay can be positioned relative to a separate anchor element, otherwise it
 * will be positioned relative to the trigger element.
 *
 * @example
 * ``` html
 * <button
 *   ftrPopover
 *   [popoverContent]="myPopover"
 *   popoverPreferredPosition="above"
 *   popoverPreferredAlignment="center"
 * >
 *   Open popover
 * </button>
 * <ng-template #myPopover>
 *   <p class="my-popover">
 *     This is a <strong>popover</strong> that can contain <button type="button">rich</button> content.
 *   </p>
 * </ng-template>
 * ```
 */
@Directive({
  selector: '[ftrPopover]',
  exportAs: 'ftrPopover',
})
export class PopoverDirective implements OnInit, OnDestroy {
  private readonly overlay = inject(Overlay)
  private readonly triggerRef = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly viewContainerRef = inject(ViewContainerRef)
  private readonly positionBuilder = inject(OverlayPositionBuilder)
  private readonly renderer = inject(Renderer2)
  private readonly destroyRef = inject(DestroyRef)

  private readonly overlayId = `ftr-popover-${generateUuid()}`
  private readonly isOpen = signal<boolean>(false)
  private portal: ComponentPortal<PopoverOutlet> | null = null
  private componentRef: ComponentRef<PopoverOutlet> | null = null
  private overlayRef: OverlayRef | null = null
  private overlayEventsCleanup: (() => void)[] = []
  private triggerEventsCleanup: (() => void)[] = []
  private mouseDownInsidePopoverElements = false
  // Used to prevent emitting to open changed output on init
  private hasInitialised = false

  /** The template to render inside the popover overlay. */
  readonly popoverContent = input.required<TemplateRef<any>>()

  /** An optional anchor element to position the popover relative to - defaults to the trigger element. */
  readonly popoverAnchor = input<ElementRef<HTMLElement> | HTMLElement>()

  /** The preferred position of the popover relative to the anchor element. Will use fallbacks if would otherwise be outside viewport. */
  readonly popoverPreferredPosition = input<PopoverPosition>('below')

  /** The preferred alignment of the popover relative to the anchor element. Will use fallbacks if would otherwise be outside viewport. */
  readonly popoverPreferredAlignment = input<PopoverAlignment>('start')

  /** The ARIA role attribute for the popover overlay. */
  readonly popoverRole = input<PopoverRole>('dialog')

  /** Whether to auto-focus the first focusable element when the popover opens. */
  readonly popoverAutoFocusOnOpen = input<boolean>(true)

  /**
   * When true (default), Enter/Space on the trigger opens the overlay while it is closed, and a
   * click on the trigger toggles the overlay. Set to false when toggling of the overlay is handled by other means.
   */
  readonly openPopoverOnClickOrKeydown = input<boolean>(true)

  /** Whether the popover should automatically close if the anchor is scrolled out of view. */
  readonly popoverCloseOnAnchorOutOfView = input<boolean>(false)

  /**
   * Opt-in fix for nested CDK overlays rendered outside this popover's DOM tree.
   *
   * Some controls inside the popover (for example inline selects) open their own CDK overlay
   * under `.cdk-overlay-container`. Those interactions are emitted by `outsidePointerEvents()` as
   * "outside" this popover, which can close it unexpectedly while the user is still interacting
   * with the nested dropdown.
   *
   * When enabled, clicks and keydown events that originate from a CDK overlay container are ignored by this
   * popover's outside close logic.
   */
  readonly popoverIgnoreCdkOverlayOutsideInteractions = input<boolean>(false)

  /** Controls which arrow keys can be used to navigate between focusable elements within the popover. */
  readonly popoverArrowNavigation = input<PopoverArrowNavigation>('both')

  /** Whether to match the popover width to the anchor element's width. */
  readonly popoverMatchAnchorWidth = input<boolean>(false)

  /** The width of the popover overlay. */
  readonly popoverWidth = input<number | string>()

  /** The minimum width of the popover overlay. */
  readonly popoverMinWidth = input<number | string>()

  /** The maximum width of the popover overlay. */
  readonly popoverMaxWidth = input<number | string>()

  /** The height of the popover overlay. */
  readonly popoverHeight = input<number | string>()

  /** The minimum height of the popover overlay. */
  readonly popoverMinHeight = input<number | string>()

  /** The maximum height of the popover overlay. */
  readonly popoverMaxHeight = input<number | string>()

  /** Horizontal offset in pixels from the calculated position. */
  readonly popoverOffsetX = input<number>(0)

  /** Vertical offset in pixels from the calculated position. */
  readonly popoverOffsetY = input<number>(0)

  /**
   * When there is no way to position the popover on screen while attached to the anchor, allow it to visually detach to
   * force the popover onto the screen
   */
  readonly popoverAllowAnchorDetach = input<boolean>(true)

  readonly popoverOpenChanged = output<boolean>()

  private readonly triggerElement = computed<HTMLElement>(() => getTriggerableElement(toHTMLElement(this.triggerRef)))
  private readonly anchorElement = computed<HTMLElement>(() => {
    const popoverAnchor = this.popoverAnchor()
    return popoverAnchor ? toHTMLElement(popoverAnchor) : toHTMLElement(this.triggerRef)
  })

  private readonly canArrowNavigateVertically = computed<boolean>(
    () => this.popoverArrowNavigation() === 'both' || this.popoverArrowNavigation() === 'vertical',
  )
  private readonly canArrowNavigateHorizontally = computed<boolean>(
    () => this.popoverArrowNavigation() === 'both' || this.popoverArrowNavigation() === 'horizontal',
  )

  private readonly scrollStrategy = this.overlay.scrollStrategies.reposition()
  private getPositionStrategy(): FlexibleConnectedPositionStrategy {
    return this.positionBuilder
      .flexibleConnectedTo(getAnchorableElement(this.anchorElement())!)
      .withFlexibleDimensions(true)
      .withGrowAfterOpen(true)
      .withPositions([this.getPreferredPosition(), ...defaultOverlayPositions])
      .withDefaultOffsetX(this.popoverOffsetX())
      .withDefaultOffsetY(this.popoverOffsetY())
      .withViewportMargin(POPOVER_VIEWPORT_MARGIN)
      .withPush(this.popoverAllowAnchorDetach())
  }

  private readonly sizeConfig = computed<OverlaySizeConfig>(() => ({
    width: this.popoverMatchAnchorWidth() ? this.anchorElement().offsetWidth : this.popoverWidth(),
    minWidth: this.popoverMinWidth(),
    maxWidth: this.popoverMaxWidth(),
    height: this.popoverHeight(),
    minHeight: this.popoverMinHeight(),
    maxHeight: this.popoverMaxHeight(),
  }))

  ngOnInit(): void {
    this.setUpTriggerAttributes()
    this.setUpTriggerEvents()
  }

  ngOnDestroy(): void {
    this.removeOverlayEvents()
    this.removeTriggerEvents()
    this.disposeOverlayRef()
  }

  openOverlay(): void {
    this.isOpen.set(true)
  }

  overlayIsOpen(): boolean {
    return this.isOpen()
  }

  closeOverlay(): void {
    this.isOpen.set(false)
  }

  toggleOverlay(): void {
    this.isOpen.update(open => !open)
  }

  focus(): void {
    focusElementInContainer(this.overlayRef?.overlayElement)
  }

  recomputePosition(): void {
    if (this.isOpen()) {
      try {
        this.overlayRef?.getConfig().positionStrategy?.apply()
      } catch {
        // Overlay might not be ready yet
      }
    }
  }

  private getPreferredPosition(): ConnectedPosition {
    const position = this.popoverPreferredPosition()
    const alignment = alignmentMap[this.popoverPreferredAlignment()]

    switch (position) {
      case 'above':
        return { originX: alignment.originX, overlayX: alignment.overlayX, originY: 'top', overlayY: 'bottom' }
      case 'left':
        return { originX: 'start', overlayX: 'end', originY: alignment.originY, overlayY: alignment.overlayY }
      case 'right':
        return { originX: 'end', overlayX: 'start', originY: alignment.originY, overlayY: alignment.overlayY }
      default:
        return { originX: alignment.originX, overlayX: alignment.overlayX, originY: 'bottom', overlayY: 'top' }
    }
  }

  private setUpOverlayRef(): void {
    this.overlayRef = this.overlay.create({
      positionStrategy: this.getPositionStrategy(),
      scrollStrategy: this.scrollStrategy,
      ...this.sizeConfig(),
    })
  }

  private disposeOverlayRef(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose()
    }
  }

  private setUpTriggerEvents(): void {
    const trigger = this.triggerElement()
    const click = this.renderer.listen(trigger, 'click', () => {
      if (!this.openPopoverOnClickOrKeydown()) {
        if (this.isOpen()) {
          this.closeOverlay()
        }
        return
      }
      this.toggleOverlay()
    })
    const mousedown = this.renderer.listen(trigger, 'mousedown', () => {
      this.mouseDownInsidePopoverElements = true
    })
    const focusout = this.renderer.listen(trigger, 'focusout', (event: FocusEvent) => this.handleFocusOut(event))
    const keydown = this.renderer.listen(trigger, 'keydown', (event: KeyboardEvent) => {
      if (this.openPopoverOnClickOrKeydown() && (event.key === 'Enter' || event.key === ' ') && !this.isOpen()) {
        event.preventDefault()
        event.stopPropagation()
        return this.openOverlay()
      }

      this.handleEscapeKey(event)
    })

    this.triggerEventsCleanup = [click, mousedown, focusout, keydown]
  }

  private removeTriggerEvents(): void {
    this.triggerEventsCleanup.forEach(cleanup => cleanup())
    this.triggerEventsCleanup = []
  }

  private setUpTriggerAttributes(): void {
    const trigger = this.triggerElement()
    const role = this.popoverRole()

    this.renderer.setAttribute(trigger, 'aria-haspopup', role)
    this.renderer.setAttribute(trigger, 'aria-expanded', String(this.isOpen()))
    this.renderer.setAttribute(trigger, 'aria-controls', this.overlayId)

    if (role === 'listbox') {
      this.renderer.setAttribute(trigger, 'role', 'combobox')
    }
  }

  private setUpOverlayEvents(): void {
    if (!this.overlayRef) return

    this.removeOverlayEvents()

    const overlayEl = this.overlayRef.overlayElement

    const outsideClick = this.overlayRef
      .outsidePointerEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        const target = event.target as HTMLElement
        if (this.popoverIgnoreCdkOverlayOutsideInteractions() && this.isInsideCdkOverlay(target)) {
          return
        }

        if (
          !this.anchorElement().contains(target) &&
          !this.triggerElement().contains(target) &&
          !this.overlayRef?.hostElement.contains(target)
        ) {
          this.closeOverlay()
        }
      })
    const keydown = this.overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.handleOverlayKeydown(event, overlayEl))
    const focusout = this.renderer.listen(overlayEl, 'focusout', (event: FocusEvent) => this.handleFocusOut(event))
    const mousedown = this.renderer.listen(overlayEl, 'mousedown', () => {
      this.mouseDownInsidePopoverElements = true
    })

    this.overlayEventsCleanup = [() => outsideClick.unsubscribe(), () => keydown.unsubscribe(), focusout, mousedown]
  }

  private isInsideCdkOverlay(target: HTMLElement | null): boolean {
    if (!target) return false
    return Boolean(target.closest('.cdk-overlay-container'))
  }

  private removeOverlayEvents(): void {
    this.overlayEventsCleanup.forEach(cleanup => cleanup())
    this.overlayEventsCleanup = []
  }

  private handleOverlayKeydown(event: KeyboardEvent, overlayEl: HTMLElement): void {
    if (event.key === 'Tab') {
      const focusables = Array.from(overlayEl.querySelectorAll(FOCUSABLE_SELECTOR))
      if (
        (event.shiftKey && event.target === focusables.at(0)) ||
        (!event.shiftKey && event.target === focusables.at(-1))
      ) {
        event.preventDefault()
        this.closeOverlay()

        if (event.shiftKey) {
          this.triggerElement().focus()
        } else {
          focusRelativeToElement(this.triggerElement(), 'next')
        }
        return
      }
    }

    if (
      (event.key === 'ArrowDown' && this.canArrowNavigateVertically()) ||
      (event.key === 'ArrowRight' && this.canArrowNavigateHorizontally())
    ) {
      event.preventDefault()
      focusElementInContainer(overlayEl, 'next', event.target)
    }

    if (
      (event.key === 'ArrowUp' && this.canArrowNavigateVertically()) ||
      (event.key === 'ArrowLeft' && this.canArrowNavigateHorizontally())
    ) {
      event.preventDefault()
      focusElementInContainer(overlayEl, 'previous', event.target)
    }

    this.handleEscapeKey(event)
  }

  private handleEscapeKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this.isOpen()) return

    event.preventDefault()
    event.stopPropagation()
    this.closeOverlay()
    this.triggerElement().focus()
  }

  private handleFocusOut(event: FocusEvent): void {
    if (!this.isOpen()) return

    const target = event.target as HTMLElement | null
    const relatedTarget = event.relatedTarget as HTMLElement | null
    if (!relatedTarget) {
      // This is used to prevent the overlay from closing when clicking on a non-focusable element
      // inside the popover elements, but still allow forward-tabbing out of the overlay to close it.
      if (this.mouseDownInsidePopoverElements) {
        this.mouseDownInsidePopoverElements = false
        return
      }
      if (this.popoverIgnoreCdkOverlayOutsideInteractions() && this.isInsideCdkOverlay(target)) {
        return
      }
      this.closeOverlay()
      return
    }

    if (
      this.triggerElement().contains(target) ||
      this.triggerElement().contains(relatedTarget) ||
      this.anchorElement().contains(target) ||
      this.anchorElement().contains(relatedTarget) ||
      this.overlayRef?.hostElement.contains(target) ||
      this.overlayRef?.hostElement.contains(relatedTarget)
    ) {
      return
    }

    this.closeOverlay()
  }

  private rebuildOverlayEffect = effect(() => {
    this.popoverContent()
    this.popoverAnchor()

    untracked(() => {
      if (this.overlayRef) {
        this.disposeOverlayRef()
        this.setUpOverlayRef()
      }
    })
  })

  private updateOverlayPositionEffect = effect(() => {
    const strategy = this.getPositionStrategy()

    untracked(() => {
      if (this.overlayRef) {
        this.overlayRef.updatePositionStrategy(strategy)
      }
    })
  })

  private updateOverlaySizeEffect = effect(() => {
    const size = this.sizeConfig()

    untracked(() => {
      if (this.overlayRef) {
        this.overlayRef.updateSize(size)
      }
    })
  })

  private updateOverlayOpenStateEffect = effect(() => {
    const isOpen = this.isOpen()

    if (isOpen) {
      if (!this.overlayRef) {
        this.setUpOverlayRef()
      }

      if (!this.overlayRef?.hasAttached()) {
        this.portal ??= new ComponentPortal(PopoverOutlet, this.viewContainerRef)
        this.componentRef = this.overlayRef!.attach(this.portal)
      }

      if (this.componentRef) {
        this.componentRef.setInput('id', this.overlayId)
        this.componentRef.setInput('content', this.popoverContent())
        this.componentRef.setInput('autoFocus', this.popoverAutoFocusOnOpen())
        this.componentRef.setInput('role', this.popoverRole())
      }

      this.setUpOverlayEvents()
    } else {
      this.removeOverlayEvents()
      this.overlayRef?.detach()
    }

    if (this.hasInitialised) {
      this.popoverOpenChanged.emit(isOpen)
    } else {
      this.hasInitialised = true
    }

    this.renderer.setAttribute(this.triggerElement(), 'aria-expanded', String(isOpen))
  })

  private intersectionObserverEffect = effect(cleanup => {
    const closeOnAnchorOutOfView = this.popoverCloseOnAnchorOutOfView()
    const anchorElement = this.anchorElement()

    if (closeOnAnchorOutOfView) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting && this.isOpen()) {
              this.closeOverlay()
            }
          })
        },
        { root: null, threshold: 0 },
      )

      observer.observe(anchorElement)

      return cleanup(() => observer.disconnect())
    }

    return undefined
  })
}

const defaultOverlayPositions: ConnectedPosition[] = [
  // Below anchor, extending right
  { originX: 'start', overlayX: 'start', originY: 'bottom', overlayY: 'top' },
  // Above anchor, extending right
  { originX: 'start', overlayX: 'start', originY: 'top', overlayY: 'bottom' },
  // Below anchor, extending left
  { originX: 'end', overlayX: 'end', originY: 'bottom', overlayY: 'top' },
  // Above anchor, extending left
  { originX: 'end', overlayX: 'end', originY: 'top', overlayY: 'bottom' },
] as const

const alignmentMap: Record<PopoverAlignment, ConnectedPosition> = {
  start: { originX: 'start', overlayX: 'start', originY: 'top', overlayY: 'top' },
  center: { originX: 'center', overlayX: 'center', originY: 'center', overlayY: 'center' },
  end: { originX: 'end', overlayX: 'end', originY: 'bottom', overlayY: 'bottom' },
} as const

function getTriggerableElement(element: HTMLElement): HTMLElement {
  if (element.matches(FOCUSABLE_SELECTOR)) {
    return element
  }
  const triggerableElement = element.querySelector(FOCUSABLE_SELECTOR)
  assertIsDefined(triggerableElement, 'No triggerable element found')
  return triggerableElement as HTMLElement
}

/**
 * Elements with display:contents can't be used as anchors, so we find the first suitable child instead
 */
function getAnchorableElement(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null

  const style = window.getComputedStyle(element)
  if (style.display !== 'contents') {
    return element
  }

  for (const child of element.children) {
    if (child.ELEMENT_NODE) {
      const result = getAnchorableElement(child as HTMLElement)
      if (result) return result
    }
  }

  return null
}
